import { useId, useMemo, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { Checkbox } from '@repo/videa-components/src/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { useAtom, useSetAtom } from 'jotai';

import type { ImageAnalyses } from '@/@types/latestImagesV2';
import type { GetClaimSummaryResponse } from '@/modules/claims/freeClaims/insuranceExport/types';
import {
  selectedImageKeysAtom,
  showAnnotationsAtom,
  showPerioChartAtom,
} from '@/modules/claims/freeClaims/state';

import { generateFmxGrid } from '../../../helpers';
import { useSelectionModalState } from '../../../hooks/useImageSelectionModalState';

import { buildInitialFilterState } from './claimImageSelectorHelpers';
import { ImageTypeDropdown } from './ImageTypeDropdown';
import { VisitDateSection } from './VisitDateSection';
import { VisitDropdown } from './VisitDropdown';

/** View filter: which image types to show in the list. Empty = show all. */
const ALL_VIEW_IMAGE_TYPE_KEYS = [
  'BITEWING',
  'PERIAPICAL',
  'PANORAMIC',
  'fmx',
  'periochart',
  'colorImages',
] as const;

type ClaimImageSelectorProps = {
  claimSummary?: GetClaimSummaryResponse;
  filteredColorImages?: ImageAnalyses[];
  filteredRadiographs?: ImageAnalyses[];
  selectedTreatmentIds: string[];
  hasQuadLevelSummary: boolean;
  showFMX: boolean;
  onSetImageSelectorOpen: (open: boolean) => void;
  onSetShowFMX: (show: boolean) => void;
};

export const ClaimImageSelector = ({
  claimSummary,
  filteredColorImages,
  filteredRadiographs,
  selectedTreatmentIds,
  hasQuadLevelSummary,
  showFMX,
  onSetImageSelectorOpen,
  onSetShowFMX,
}: ClaimImageSelectorProps) => {
  const fmxGrid = useMemo(
    () => generateFmxGrid(claimSummary, selectedTreatmentIds ?? []),
    [claimSummary, selectedTreatmentIds],
  );

  const showAnnotationsId = useId();
  const [showAnnotationsFromAtom] = useAtom(showAnnotationsAtom);
  const setShowAnnotations = useSetAtom(showAnnotationsAtom);
  const [selectedImageKeys] = useAtom(selectedImageKeysAtom);
  const [showPerioChart] = useAtom(showPerioChartAtom);

  // Local state so annotation toggle is deferred until Apply (consistent with other modal state)
  const [localShowAnnotations, setLocalShowAnnotations] = useState(
    showAnnotationsFromAtom,
  );
  const initialFilterState = useMemo(
    () =>
      buildInitialFilterState({
        filteredRadiographs: filteredRadiographs ?? [],
        filteredColorImages: filteredColorImages ?? [],
        fmxGrid: fmxGrid ?? [],
        selectedImageKeys,
        hasQuadLevelSummary,
        claimSummary,
        showFMX,
        showPerioChart,
      }),
    [
      filteredRadiographs,
      filteredColorImages,
      fmxGrid,
      selectedImageKeys,
      hasQuadLevelSummary,
      claimSummary,
      showFMX,
      showPerioChart,
    ],
  );

  const { filterState, actions, selectors, onModalClose } =
    useSelectionModalState({
      initialFilterState,
      publishToAtoms: true,
      setShowFMX: onSetShowFMX,
    });

  const allDates = useMemo(
    () => Object.keys(filterState).sort((a, b) => b.localeCompare(a)),
    [filterState],
  );

  // View filter: which types/dates to show. Empty set = show all.
  const [viewFilterImageTypes, setViewFilterImageTypes] = useState<Set<string>>(
    () => new Set(ALL_VIEW_IMAGE_TYPE_KEYS),
  );
  const [viewFilterDates, setViewFilterDates] = useState<Set<string>>(
    () => new Set(),
  );

  const visibleDates =
    viewFilterDates.size === 0 ? allDates : allDates.filter((d) => viewFilterDates.has(d));

  const filteredRadiographsList = filteredRadiographs ?? [];
  const filteredColorImagesList = filteredColorImages ?? [];

  const toggleViewFilterImageType = (key: string) => {
    setViewFilterImageTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleViewFilterDate = (dateStr: string) => {
    setViewFilterDates((prev) => {
      const effective = prev.size === 0 ? new Set(allDates) : new Set(prev);
      const next = new Set(effective);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next.size === allDates.length ? new Set<string>() : next;
    });
  };

  const isViewFilterDateChecked = (dateStr: string) =>
    viewFilterDates.size === 0 || viewFilterDates.has(dateStr);

  const isViewFilterImageTypeChecked = (key: string) =>
    viewFilterImageTypes.has(key);

  return (
    <Dialog
      defaultOpen
      onOpenChange={(open) => {
        if (!open) onSetImageSelectorOpen(false);
      }}
    >
      <DialogContent className="vai-max-w-4xl vai-w-full vai-p-0 vai-gap-0 vai-overflow-hidden">
        <div className="vai-flex vai-flex-col vai-gap-4 vai-max-h-[80vh] vai-overflow-hidden vai-bg-white">
          <DialogHeader className="vai-flex-row vai-items-center vai-space-y-0">
            <DialogTitle>
              <Trans>Select Images</Trans>
            </DialogTitle>
          </DialogHeader>

          {/* Filter bar */}
          <div className="vai-flex vai-flex-wrap vai-items-center vai-gap-3">
            <ImageTypeDropdown
              viewFilterImageTypes={viewFilterImageTypes}
              allTypeKeys={ALL_VIEW_IMAGE_TYPE_KEYS}
              onToggleType={toggleViewFilterImageType}
              isTypeChecked={isViewFilterImageTypeChecked}
            />
            <VisitDropdown
              filterState={filterState}
              allDates={allDates}
              visibleDates={visibleDates}
              datePresentCount={selectors.datePresentCount}
              isDateChecked={isViewFilterDateChecked}
              onToggleDate={toggleViewFilterDate}
            />
            <label
              htmlFor={showAnnotationsId}
              className="vai-flex vai-items-center vai-gap-2 vai-cursor-pointer vai-text-sm vai-font-normal vai-select-none"
            >
              <Checkbox
                id={showAnnotationsId}
                checked={localShowAnnotations}
                onCheckedChange={(checked) =>
                  setLocalShowAnnotations(checked === true)
                }
              />
              <Trans>Include AI annotated images in claim</Trans>
            </label>
            <span className="vai-flex-1" aria-hidden />
            <Button
              variant="outline"
              size="sm"
              onClick={() => actions.clearAll()}
            >
              <Trans>Clear Selection</Trans>
            </Button>
          </div>

          <div className="vai-flex-1 vai-min-h-0 vai-min-w-0 vai-overflow-auto vai-space-y-6 vai-pb-4 vai-border vai-border-slate-200 vai-rounded-md">
            {visibleDates.map((dateStr) => {
              const filters = filterState[dateStr];
              if (!filters) return null;
              return (
                <VisitDateSection
                  key={dateStr}
                  dateStr={dateStr}
                  filters={filters}
                  selectors={selectors}
                  filteredRadiographs={filteredRadiographsList}
                  filteredColorImages={filteredColorImagesList}
                  claimSummary={claimSummary}
                  selectedTreatmentIds={selectedTreatmentIds}
                  fmxGrid={fmxGrid ?? []}
                  actions={actions}
                  viewFilterImageTypes={viewFilterImageTypes}
                />
              );
            })}
          </div>

          <DialogFooter className="vai-flex-row vai-justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => actions.revertToInitial(initialFilterState)}
            >
              <Trans>Revert to default selections</Trans>
            </Button>
            <Button
              size="sm"
              onClick={() => {
                onModalClose();
                setShowAnnotations(localShowAnnotations);
                onSetImageSelectorOpen(false);
              }}
            >
              <Trans>Apply</Trans>
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
